import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/UIKit";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Search, CheckCircle2, AlertTriangle, Building2, User, ExternalLink } from "lucide-react";

export default function NyKunde() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("privat");
  const [saving, setSaving] = useState(false);
  const [brregBusy, setBrregBusy] = useState(false);
  const [brregData, setBrregData] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", address: "", postal_code: "", city: "",
    fnr: "", orgnr: "", contact_person: "", invoice_email: "", ehf_enabled: false,
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupBrreg = async () => {
    if (!form.orgnr || form.orgnr.length !== 9) {
      toast.error("Skriv 9 sifre for organisasjonsnummer");
      return;
    }
    setBrregBusy(true); setBrregData(null); setDuplicate(null);
    try {
      // Sjekk duplikat først
      const dup = await api.post("/kraftadmin/check-duplicate", { orgnr: form.orgnr });
      if (dup.data.exists) {
        setDuplicate(dup.data.matches[0]);
        toast.warning("Denne kunden finnes allerede");
        return;
      }
      // Hent fra Brreg
      const { data } = await api.get(`/kraftadmin/brreg/${form.orgnr}`);
      setBrregData(data);
      setForm((f) => ({
        ...f,
        full_name: data.full_name || f.full_name,
        address: data.address || f.address,
        postal_code: data.postal_code || f.postal_code,
        city: data.city || f.city,
      }));
      if (data.konkurs) toast.warning("Merk: selskapet er registrert konkurs");
      else if (data.under_avvikling) toast.warning("Merk: selskapet er under avvikling");
      else toast.success(`Hentet: ${data.full_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brreg-oppslag feilet");
    } finally { setBrregBusy(false); }
  };

  const checkEmail = async () => {
    if (!form.email) return;
    const dup = await api.post("/kraftadmin/check-duplicate", { email: form.email });
    if (dup.data.exists) {
      setDuplicate(dup.data.matches[0]);
      toast.warning("En kunde med denne e-posten finnes allerede");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (duplicate) {
      toast.error("Kan ikke opprette — kunde finnes allerede");
      return;
    }
    setSaving(true);
    try {
      const payload = { kind, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.post("/customers", payload);
      toast.success(`Kunde ${data.customer_number} opprettet automatisk`);
      navigate(`/kunder/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette kunde");
    } finally { setSaving(false); }
  };

  const field = (name, label, extra = {}) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</label>
      <input value={form[name]} onChange={(e) => upd(name, e.target.value)}
        className="w-full h-10 px-3 rounded-md bg-zinc-900/60 border border-zinc-900 focus:border-emerald-500/40 text-sm text-zinc-100 focus:outline-none"
        data-testid={`ny-kunde-${name}`} {...extra} />
    </div>
  );

  return (
    <div data-testid="ny-kunde-page">
      <button onClick={() => navigate(-1)} className="text-sm text-zinc-400 hover:text-zinc-200 flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> Tilbake
      </button>
      <PageHeader title="Ny kunde" subtitle="Automatisert registrering med Brreg-oppslag og duplikatkontroll" />

      <Card className="p-6 max-w-3xl">
        <form onSubmit={submit} className="space-y-5">
          <div className="flex gap-2">
            {[{ v: "privat", label: "Privatkunde", icon: User }, { v: "bedrift", label: "Bedriftskunde", icon: Building2 }].map((o) => (
              <button key={o.v} type="button" onClick={() => { setKind(o.v); setBrregData(null); setDuplicate(null); }}
                className={`px-4 py-2 rounded-md text-sm border transition-colors flex items-center gap-2 ${
                  kind === o.v ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-zinc-900 text-zinc-400 hover:border-zinc-800"
                }`} data-testid={`kind-${o.v}`}>
                <o.icon className="h-4 w-4" /> {o.label}
              </button>
            ))}
          </div>

          {kind === "bedrift" && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <div className="text-[11px] uppercase text-emerald-400 mb-1.5 font-medium">Steg 1: Automatisk oppslag</div>
              <div className="flex gap-2">
                <input value={form.orgnr} onChange={(e) => upd("orgnr", e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="Organisasjonsnummer (9 sifre)" maxLength={9} inputMode="numeric"
                  className="flex-1 h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm font-mono tabular-nums" data-testid="ny-kunde-orgnr" />
                <button type="button" onClick={lookupBrreg} disabled={brregBusy || form.orgnr.length !== 9}
                  className="h-10 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center gap-2" data-testid="brreg-lookup">
                  {brregBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Hent fra Brreg
                </button>
              </div>
              {brregData && (
                <div className="mt-3 text-xs text-zinc-400 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  {brregData.full_name} · {brregData.organisasjonsform || "—"} · {brregData.naeringskode || "—"}
                  {brregData.konkurs && <span className="text-red-400 ml-2">⚠ Konkurs</span>}
                  {brregData.registrert_i_mva && <span className="text-emerald-400 ml-2">MVA-registrert</span>}
                </div>
              )}
            </div>
          )}

          {duplicate && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4" data-testid="duplicate-warning">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-amber-300">Denne kunden finnes allerede</div>
                  <div className="text-xs text-zinc-400 mt-1">{duplicate.customer_number} · {duplicate.full_name}</div>
                  <div className="flex gap-2 mt-3">
                    <button type="button" onClick={() => navigate(`/kunder/${duplicate.id}`)}
                      className="h-8 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-medium flex items-center gap-1.5">
                      <ExternalLink className="h-3 w-3" /> Åpne eksisterende kunde
                    </button>
                    <button type="button" onClick={() => setDuplicate(null)}
                      className="h-8 px-3 rounded-md border border-zinc-800 text-xs text-zinc-400">Fortsett likevel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field("full_name", kind === "bedrift" ? "Firmanavn" : "Fullt navn", { required: true })}
            {kind === "privat" && field("fnr", "Fødselsnummer (11 sifre)", { maxLength: 11, inputMode: "numeric" })}
            <div className="md:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">E-post</label>
                <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} onBlur={checkEmail}
                  className="w-full h-10 px-3 rounded-md bg-zinc-900/60 border border-zinc-900 text-sm" data-testid="ny-kunde-email" />
              </div>
              {field("phone", "Telefon")}
            </div>
            {field("address", "Adresse")}
            <div className="grid grid-cols-2 gap-4">
              {field("postal_code", "Postnr")}
              {field("city", "Poststed")}
            </div>
            {kind === "bedrift" && field("contact_person", "Kontaktperson")}
            {kind === "bedrift" && field("invoice_email", "Fakturaadresse (e-post)", { type: "email" })}
          </div>

          {kind === "bedrift" && (
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={form.ehf_enabled} onChange={(e) => upd("ehf_enabled", e.target.checked)} />
              Send fakturaer som EHF (PEPPOL)
            </label>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving || !!duplicate}
              className="h-10 px-5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black text-sm font-medium flex items-center gap-2" data-testid="save-kunde">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Opprett kunde
            </button>
            <button type="button" onClick={() => navigate(-1)} className="h-10 px-4 rounded-md border border-zinc-900 hover:border-zinc-800 text-zinc-400 text-sm">
              Avbryt
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
